<template>
  <div
    v-if="!!$slots.default || !hidden"
    v-viewed.once="{func: collectionViewed, params: [{beforeMargin: '2000', afterMargin: '0'}]}"
    class="dn-collection"
  >
    <template v-if="!effectiveLazy || isCollectionViewed || showSkeleton || collection.length > 0">
      <slot v-bind="{ collection, metadata, loading: showSkeleton }" />
    </template>
  </div>
</template>
<script lang="ts" setup>
const props = defineProps({
  cid: {
    type: String,
    required: false,
    default: ''
  },
  provider: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: false,
    default: () => {}
  },
  mood: {
    type: String,
    required: false,
    default: 'clear'
  },
  masked: {
    type: Boolean,
    required: false,
    default: true
  },
  awaitFor: {
    type: Array,
    required: false,
    default: () => []
  },
  fnc: {
    type: Function,
    required: false,
    default: () => {}
  },
  suppressEmit: {
    type: Boolean,
    required: false,
    default: false
  },
  suppressSkipIds: {
    type: Boolean,
    required: false,
    default: false
  }
});
const lazy = inject('lazy', false);

const isBackNavigation = ref(false);

const effectiveLazy = computed(() => {
  return isBackNavigation.value ? false : lazy;
});

const calcId = props.cid || self.crypto.randomUUID();
const globalSkipIds = inject('global_skip_ids', ref([]));

const globalAwaitForList: any = inject('global_await_for_list', ref([]));

const globalStatusList = inject('global_status_list', ref([]));

const awaitIdsFromGlobalList = globalAwaitForList?.value[calcId] || [];

const items = ref([]);
const enabled = ref(false);
const canEnable = ref(false);
const isCollectionViewed = ref(false);
const defaultOptions = {
  size: 10,
  offset: 0
};

const options = computed(() => {
  return getOptions(
    {
      provider: props.provider,
      options: updatedOptions.value
    },
    defaultOptions
  );
});

const dynamicOptions = ref({});
const updatedOptions = computed(() => {
  return { ...props.options, ...dynamicOptions.value };
});

const { getData, getOptions, getFirebaseData } = useCollection();
const loading = inject('loading', ref(false));
const result = ref<any>();
const previousState = ref([]);
const requestOptions = {
  id: calcId,
  options,
  enabled,
  skipIds: (props.options?.removeUsed && !props.suppressSkipIds) ? globalSkipIds : ref([])
};

const runRequest = () => {
  result.value = props.provider?.toLowerCase()?.includes('firebase')
    ? getFirebaseData(props.provider, requestOptions)
    : getData(props.provider, requestOptions);
};

const router = useRouter();
const route = useRoute();

onMounted(() => {
  const hasHistoryScroll = !!(window.history.state && window.history.state.scroll);
  const savedPosition = router.options.scrollBehavior?.(route, router.currentRoute.value, window.history.state?.scroll);
  const hasSavedPosition = !!(savedPosition && savedPosition.top > 0);
  if (hasHistoryScroll || hasSavedPosition) {
    isBackNavigation.value = true;
  }
  useEventListener('popstate', () => {
    if (!isBackNavigation.value) {
      isBackNavigation.value = true;
    }
  });
});

const collection = computed(() => {
  /*
   * The watcher below populates `previousState`, but `flush: 'post'` watchers
   * do not run during SSR. Derive items straight from the response when
   * previousState is still empty so embedded teasers mount (and prefetch) on
   * the server.
   */
  if (previousState.value.length) { return previousState.value; }
  const response = result.value?.response;
  return Array.isArray(response)
    ? response
    : Array.isArray(response?.items)
      ? response.items
      : previousState.value;
});

const showSkeleton = computed(() => !!result.value?.loading && collection.value.length === 0);

/*
 * Exposed via the slot scope so children can read metadata during SSR.
 * `props.fnc` only fires from the `flush: 'post'` watch below, which does not
 * run on the server, so metadata must be derived from the response directly.
 */
const metadata = computed(() => {
  const response = result.value?.response;
  return Array.isArray(response) ? undefined : response?.metadata;
});

watch(
  () => result.value?.response,
  (response) => {
    const responseItems = Array.isArray(response)
      ? [...response]
      : Array.isArray(response?.items)
        ? [...response.items]
        : null;

    if (!responseItems) { return; }

    if (props.mood === 'clear') {
      previousState.value = [...responseItems];
    } else if (props.mood === 'append') {
      previousState.value.push(...responseItems);
    } else if (props.mood === 'prepend') {
      const collectionItems = [...previousState.value];
      collectionItems.unshift(...responseItems);
      previousState.value = collectionItems;
    }
    items.value = previousState.value;
    props.fnc(items.value);
    if (!props.suppressEmit) {
      useBusEmitter(`DnCollection__collection__${props.cid}`, {
        items: items.value,
        total: result.value?.response?.total,
        size: result.value?.response?.size,
        updatedAt: result.value?.response?.updated_at
      });
    }
  },
  { immediate: true, flush: 'post' }
);

watch(() => result.value?.loading, (newLoading) => {
  if (newLoading !== undefined) {
    loading.value = newLoading;
  }
  if (newLoading === false) {
    const response = result.value?.response;
    const hasData = Array.isArray(response) || Array.isArray(response?.items);
    if (!hasData && !props.suppressEmit) {
      useBusEmitter(`DnCollection__collection__${props.cid}`, { items: [] });
    }
  }
}, { immediate: true });

const awaitedIds = [...props.awaitFor, ...awaitIdsFromGlobalList]
  .filter((value, _, self) => self.indexOf(value) === self.lastIndexOf(value));

let cleanup: (() => void)[] = [];

if (awaitedIds?.length > 0) {
  const receivedIds = [];
  awaitedIds.forEach((id) => {
    if (globalStatusList?.value[id].load === true) {
      receivedIds.push(id);
    } else {
      const listenerCleanup = useBusListener({
        [`DnCollection__collection__${id}`]: (_) => {
          receivedIds.push(id);
          if (awaitedIds.length === receivedIds.length) {
            lazy ? canEnable.value = true : enabled.value = true;
          }
          listenerCleanup();
        }
      });
      cleanup.push(listenerCleanup);
    }

    if (awaitedIds.length === receivedIds.length) {
      lazy ? canEnable.value = true : enabled.value = true;
    }
  });
} else {
  lazy ? canEnable.value = true : enabled.value = true;
}
runRequest();

onBeforeMount(() => {
  if (lazy) {
    const listenerCleanup = useBusListener({
      [`DnCollection__collectionFetch__${props.cid}`]: (_) => {
        watch(canEnable, (newVal) => {
          if (newVal) {
            enabled.value = true;
          }
        }, {
          immediate: true
        });
        listenerCleanup();
      }
    });
    cleanup.push(listenerCleanup);
  }
});
let collectionViewed = () => {};
onBeforeMount(() => {
  collectionViewed = () => {
    if (awaitedIds?.length > 0) {
      awaitedIds.forEach((id) => {
        if (!globalStatusList?.value[id].load) {
          useBusEmitter(`DnCollection__collectionFetch__${id}`);
        }
      });
    }
    useBusEmitter(`DnCollection__collectionFetch__${props.cid}`);
    isCollectionViewed.value = true;
  };
});

cleanup.push(useBusListener({
  [`collection__options__${props.cid}`]: (data) => {
    dynamicOptions.value = { ...dynamicOptions.value, ...data };
  }
}));

onBeforeUnmount(() => {
  if (cleanup && cleanup.length > 0) {
    cleanup.forEach(fn => fn());
    cleanup = [];
  }
});

const hidden = computed(() => !props.masked && loading.value);
</script>
<style lang="scss">
.dn-collection {
  --_dn-collection_grid-template: var(--dn-collection_grid-template, auto / auto);
  --_dn-collection_gap: var(--dn-collection_gap, 12px);
  --_dn-collection-top_grid-area: var(--dn-collection-top_grid-area, auto);
  --_dn-collection-top_content-width: var(--dn-collection-top_content-width, 980px);
  --_dn-collection_fill: var(--dn-collection_fill);
  --_dn-collection_border-radius: var(--dn-collection_border-radius, var(--box-radius));
  --_dn-collection_spacing: var(--dn-collection_spacing, 0);

  display: grid;
  grid-template: var(--_dn-collection_grid-template);
  gap: var(--_dn-collection_gap);
  background: var(--_dn-collection_fill);
  border-radius: var(--_dn-collection_border-radius);
  padding: var(--_dn-collection_spacing);

  &.dn-topic-collection,
  &.dn-tag-collection,
  &.dn-section-collection,
  &.dn-category-collection {
    grid-template: var(--_dn-collection_grid-template);
  }

  &-top {
    display: grid;
    grid-area: var(--_dn-collection-top_grid-area);
    gap: var(--_dn-collection_gap);

    @media only screen and (width >= 1300px) {
      grid-template: auto / var(--_dn-collection-top_content-width);
    }
  }

  .header {
    --dn-headline_font-family: var(--dn-collection--header_font-family);
    --dn-headline_font-weight: var(--dn-collection--header_font-weight, 500);
    --dn-headline_font-size: var(--dn-collection--header_font-size, 20px);
    --dn-headline_line-height: var(--dn-collection--header_line-height, 100%);
    --dn-headline_border-bottom-width: var(--dn-collection--header_border-bottom-width, 0);
    --dn-headline_border-bottom-color: var(--dn-collection--header_border-bottom-color, transparent);
    --dn-headline_spacing: var(--dn-collection--header_spacing, 0);
  }

  .dn-group,
  .dn-placeholder {
    &:has(.logo) {
      --dn-group-header_spacing: 20px;
      --dn-placeholder-header_spacing: 8px;

      @media only screen and (width >= 980px) {
        --dn-group-header_spacing: 24px;
        --dn-placeholder-header_spacing: 12px;
      }
    }
  }

  .dn-grid {
    --dn-teaser-card--title_prefered_font-size: var(--teaser-prefered-font-size);
    --dn-teaser-card--title_min_font-size: var(--teaser-medium-min-font-size);
    --dn-teaser-card--title_max_font-size: var(--teaser-medium-max-font-size);
    --dn-skeleton_lines_border-bottom-width: 1px;
    --dn-skeleton_lines_padding: 0 0 var(--teaser-spacing);

    &-full {
      &:not(.dn-inline-ad, .dn-ad-preview, .dn-job-collection, .dn-read-peak-ad) {
        @media only screen and (width >= 1328px) {
          inline-size: calc(100vw - var(--dn-collection-content-padding, 0px) * 2);
          margin-left: calc(-50vw + var(--dn-collection-content-width, 980px) / 2);
        }
      }
    }

    .dn-card {
      --dn-teaser-card--title_font-size: clamp(var(--dn-teaser-card--title_min_font-size), var(--dn-teaser-card--title_prefered_font-size), var(--dn-teaser-card--title_max_font-size));
    }

    .dn-teaser-card {
      --dn-card_assets_ratio: auto;
      --dn-teaser-card--card_font-family: var(--typography-card-small-font-family);
      --dn-teaser-card--link-text-decoration: none;
      --dn-teaser-card--badge_background: var(--color-teaser-standard-bg);
      --dn-teaser-card--link_display: grid;
      --dn-teaser-card--link-color: var(--color-teaser-standard-fg);
      --dn-teaser-card--link-color__hover: var(--color-teaser-standard-fg);
      --dn-teaser-card--link-color__active: var(--color-teaser-standard-fg);
      --dn-teaser-card--card_row-gap: var(--row-gap-section);
      --dn-teaser-card--card_border-bottom-color: var(--color-teaser-standard-divider);
      --dn-teaser-card--card_padding: 0 0 var(--teaser-spacing);
      --dn-teaser-card--title_strong_font-family: var(--typography-card-large-font-family);
      --dn-teaser-card--title_strong_font-weight: var(--fontweight-bold);
      --dn-teaser-card--title_regular_font-family: var(--typography-card-default-font-family);
      --dn-teaser-card--title_regular_font-weight: var(--fontweight-regular);
      --dn-teaser-card--title_line-height: var(--teaser-line-height);
      --dn-teaser-card--title_max-width: var(--teaser-card-title-max-width);
      --dn-teaser-card--lead_font-family: var(--typography-editorial-lead-min-font-family);
      --dn-teaser-card--lead_font-size: var(--typography-card-lead-font-size, var(--typography-editorial-lead-min-font-size));
      --dn-teaser-card--lead_font-weight: var(--typography-editorial-lead-min-font-weight);
      --dn-teaser-card--lead_line-height: var(--typography-editorial-lead-min-line-height);
      --dn-teaser-card--lead_max-width: var(--teaser-card-lead-max-width);
      --dn-teaser-card--kicker_display: block;
      --dn-teaser-card--kicker_font-family: var(--typography-utility-m-font-family);
      --dn-teaser-card--kicker_font-size: var(--typography-utility-m-font-size);
      --dn-teaser-card--kicker_font-weight: var(--typography-utility-m-font-weight);
      --dn-teaser-card--kicker_color: var(--color-teaser-standard-fg);
      --dn-teaser-card--kicker_line-height: 100%;
      --dn-teaser-card--kicker_spacing: 4px 0 0;
      --dn-teaser-card--meta_font-family: var(--typography-utility-s-font-family);
      --dn-teaser-card--meta_color: var(--color-teaser-standard-subtle-fg);
      --dn-teaser-card--meta_text-transform: var(--typography-utility-topic-text-case);
      --dn-teaser-card--meta_spacing: var(--row-gap-section) 0 0;
      --dn-teaser-card--badge_border-radius: var(--box-radius);
      --dn-teaser-card--badge_font-family: var(--typography-utility-s-font-family);
      --dn-teaser-card--badge_font-size: var(--typography-utility-s-font-size);
      --dn-teaser-card--badge_spacing: 0 var(--teaser-spacing) var(--teaser-spacing);
      --dn-teaser-card--badge_spacing-inner: 6px 8px;
      --teaser-card--card_border-radius: var(--border-radius-teaser-min);
      --teaser-card--card_row-gap: 8px;
      --dn-teaser-video-card--badge_background: var(--color-video-duration);
      --dn-teaser-video-card--assets_overlay: var(--color-video-overlay);
      --dn-teaser-video-card--badge_color: #fff;
      --dn-teaser-video-card--badge_font-weight: 400;

      @media only screen and (width >= 980px) {
        --dn-card_assets_ratio: auto;
        --dn-teaser-card--card_grid-template: repeat(6, min-content) 1fr / repeat(12, 1fr);
        --dn-teaser-card--card_title_grid-area: auto / 1 / span 1 / 13;
        --teaser-card--card_border-radius: var(--border-radius-teaser-max);
        --dn-teaser-card--title_line-height: var(--teaser-line-height);
        --dn-teaser-card--lead_line-height: var(--typography-card-large-line-height);
      }

      &.dn-title-flex {
        --dn-teaser-card--title_line-height: var(--teaser-flex-line-height);
        --dn-teaser-card--title_white-space: nowrap;
      }

      &.dn-image-hide {
        --dn-teaser-card--card_grid-template: repeat(1, min-content) 1fr / repeat(12, 1fr);
        --dn-teaser-card--card_meta_grid-area: auto-fill / 1 / span 1 / 13;
        --dn-teaser-card--meta_spacing: 0;
      }

      &.brand,
      &.highlight,
      &.inverted,
      &.opinion,
      &.brand-secondary,
      &.feature {
        --dn-teaser-card--card_border-radius: var(--box-radius);
        --dn-teaser-card--card_image_border-radius: var(--box-radius) 0 0 var(--box-radius);
        --dn-teaser-card--card_border-bottom-width: 0;
        --dn-teaser-card--title_spacing: 0 var(--row-gap-section);
        --dn-teaser-card--lead_spacing: 0 var(--row-gap-section) 0 0;
        --dn-teaser-card--meta_spacing: var(--row-gap-section) var(--row-gap-section) var(--row-gap-section) 0;
        --dn-teaser-card--kicker_spacing: 0 var(--row-gap-section);
        --dn-teaser-card--strapline_spacing: 0 var(--row-gap-section) 0 0;

        &.dn-image-hide {
          --dn-teaser-card--card_padding: var(--teaser-spacing) 0;
        }

        :has(.kicker) {
          --dn-teaser-card--title_spacing: 0 var(--row-gap-section);
        }

        @media only screen and (width >= 980px) {
          --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
          --dn-teaser-card--title_spacing: 0 var(--teaser-spacing);
          --dn-teaser-card--lead_spacing: 0 var(--teaser-spacing);
          --dn-teaser-card--meta_spacing: var(--row-gap-section) var(--teaser-spacing) 0;
          --dn-teaser-card--kicker_spacing: 4px var(--teaser-spacing) 0;
          --dn-teaser-card--strapline_spacing: 0 var(--teaser-spacing);

          &.dn-image-hide {
            --dn-teaser-card--meta_spacing: 0 var(--row-gap-section);
          }

          :has(.kicker) {
            --dn-teaser-card--title_spacing: 0 var(--teaser-spacing);
          }
        }
      }

      &.dn-teaser-card-feature {
        --dn-teaser-card--card_kicker_display: block;
        --dn-teaser-card--title_text-align: center;
        --dn-teaser-card--card_meta_justify-content: center;
        --dn-teaser-card--kicker_text-align: center;
        --dn-teaser-card--lead_text-align: center;

        &.special {
          --dn-teaser-card--badge_text-align: center;
        }
      }

      &.brand {
        --dn-teaser-card--card_background: var(--color-teaser-brand-bg);
        --dn-teaser-card--link-color: var(--color-teaser-brand-fg);
        --dn-teaser-card--link-color__hover: var(--color-teaser-brand-fg);
        --dn-teaser-card--link-color__active: var(--color-teaser-brand-fg);
        --dn-teaser-card--meta_color: var(--color-teaser-brand-subtle-fg);
        --dn-teaser-card--kicker_color: var(--color-teaser-brand-fg);
        --dn-teaser-card--kicker-icon_color: var(--color-teaser-brand-fg);
        --dn-teaser-card--date-time_color: var(--color-teaser-brand-fg);
      }

      &.brand-secondary {
        --dn-teaser-card--card_background: var(--color-teaser-brand-secondary-bg);
        --dn-teaser-card--link-color: var(--color-teaser-brand-secondary-fg);
        --dn-teaser-card--link-color__hover: var(--color-teaser-brand-secondary-fg);
        --dn-teaser-card--link-color__active: var(--color-teaser-brand-secondary-fg);
        --dn-teaser-card--meta_color: var(--color-teaser-brand-secondary-subtle-fg);
        --dn-teaser-card--kicker_color: var(--color-teaser-brand-secondary-fg);
        --dn-teaser-card--date-time_color: var(--color-teaser-brand-secondary-fg);
      }

      &.feature {
        --dn-teaser-card--card_background: var(--color-teaser-feature-bg);
        --dn-teaser-card--link-color: var(--color-teaser-feature-fg);
        --dn-teaser-card--link-color__hover: var(--color-teaser-feature-fg);
        --dn-teaser-card--link-color__active: var(--color-teaser-feature-fg);
        --dn-teaser-card--meta_color: var(--color-teaser-feature-fg);
        --dn-teaser-card--kicker_color: var(--color-teaser-feature-fg);
        --dn-teaser-card--date-time_color: var(--color-teaser-feature-fg);
      }

      &.highlight {
        --dn-teaser-card--card_background: var(--color-teaser-highlight-bg);
        --dn-teaser-card--link-color: var(--color-teaser-highlight-fg);
        --dn-teaser-card--link-color__hover: var(--color-teaser-highlight-fg);
        --dn-teaser-card--link-color__active: var(--color-teaser-highlight-fg);
        --dn-teaser-card--meta_color: var(--color-teaser-highlight-subtle-fg);
        --dn-teaser-card--kicker_color: var(--color-teaser-highlight-fg);
        --dn-teaser-card--kicker-icon_color: var(--color-teaser-highlight-fg);
        --dn-teaser-card--date-time_color: var(--color-teaser-highlight-fg);
      }

      &.inverted {
        --dn-teaser-card--card_background: var(--color-teaser-inverted-bg);
        --dn-teaser-card--link-color: var(--color-teaser-inverted-fg);
        --dn-teaser-card--link-color__hover: var(--color-teaser-inverted-fg);
        --dn-teaser-card--link-color__active: var(--color-teaser-inverted-fg);
        --dn-teaser-card--meta_color: var(--color-teaser-inverted-subtle-fg);
        --dn-teaser-card--kicker_color: var(--color-teaser-inverted-fg);
        --dn-teaser-card--kicker-icon_color: var(--color-teaser-inverted-fg);
        --dn-teaser-card--date-time_color: var(--color-teaser-inverted-fg);
      }

      &.opinion {
        --dn-teaser-card--card_background: var(--color-teaser-opinion-bg);
        --dn-teaser-card--link-color: var(--color-teaser-opinion-fg);
        --dn-teaser-card--link-color__hover: var(--color-teaser-opinion-fg);
        --dn-teaser-card--link-color__active: var(--color-teaser-opinion-fg);
        --dn-teaser-card--meta_color: var(--color-teaser-opinion-subtle-fg);
        --dn-teaser-card--kicker_color: var(--color-teaser-opinion-fg);
        --dn-teaser-card--kicker-icon_color: var(--color-teaser-opinion-fg);
        --dn-teaser-card--date-time_color: var(--color-teaser-opinion-subtle-fg);
      }

      &.opinion,
      &.opinion-font {
        --dn-teaser-card--title_font-family: var(--typography-card-opinion-font-family);
        --dn-teaser-card--title_min_font-size: var(--teaser-opinion-medium-min-font-size);
        --dn-teaser-card--title_max_font-size: var(--teaser-opinion-medium-max-font-size);
        --dn-teaser-card--title_prefered_font-size: var(--teaser-opinion-prefered-font-size);
        --dn-teaser-card--title_line-height: var(--teaser-opinion-line-height);
        --dn-teaser-card--title_font-weight: var(--typography-card-opinion-font-weight);
        --dn-teaser-card--title_strong_font-family: var(--typography-card-opinion-font-family);
        --dn-teaser-card--title_strong_font-weight: 700;
        --dn-teaser-card--title_regular_font-family: var(--typography-card-opinion-font-family);
        --dn-teaser-card--title_regular_font-weight: 400;
      }

      &.special {
        --teaser-spacing: 24px;
        --dn-teaser-card--card_grid-template: 1fr / repeat(12, 1fr);
        --dn-teaser-card--card_border-radius: var(--border-radius-teaser-max);
        --dn-teaser-card--card_border-bottom-width: 0;
        --dn-teaser-card--card_padding: 0;
        --dn-teaser-card--card_assets_overlay: linear-gradient(180deg, rgb(6 14 30 / 0%) 0%, rgb(6 14 30 / 0%) 50%, rgb(6 14 30 / 100%) 100%);
        --dn-teaser-card--badge_spacing: var(--teaser-spacing);
        --dn-teaser-card--card_badge_align-content: flex-start;
        --dn-teaser-card--card_image_border-radius: var(--box-radius);
        --dn-teaser-card--card_background: var(--color-teaser-inverted-bg);
        --dn-teaser-card--link-color: var(--color-brand-utility-0);
        --dn-teaser-card--link-color__hover: var(--color-brand-utility-0);
        --dn-teaser-card--link-color__active: var(--color-brand-utility-0);
        --dn-teaser-card--meta_color: var(--color-brand-utility-200);
        --dn-teaser-card--kicker_color: var(--color-brand-utility-0);
        --dn-teaser-card--kicker-icon_color: var(--color-brand-utility-0);
        --dn-teaser-card--lead_spacing: 0 var(--teaser-spacing);
        --dn-teaser-card--title_spacing: 0 var(--teaser-spacing);
        --dn-teaser-card--title_line-height: var(--typography-card-large-line-height);
        --dn-teaser-card--meta_spacing: var(--row-gap-section) var(--teaser-spacing) var(--teaser-spacing);
        --dn-teaser-card--kicker_spacing: 0 var(--teaser-spacing);
        --dn-teaser-card--strapline_spacing: 0 var(--teaser-spacing);
        --dn-card_badge_grid-area: 1 / 1 / span 1 / 13;
        --dn-card_assets_grid-area: 1 / 1 / 5 / 13;
        --dn-card_title_grid-area: 3 / 1 / span 1 / 13;
        --dn-card_meta_grid-area: 4 / 1 / span 1 / 13;
        --dn-card_kicker_grid-area: 2 / 1 / span 1 / 13;

        @media only screen and (width >= 980px) {
          --teaser-spacing: 32px;
        }

        &[class*='dn-image-format-'] {
          --dn-card_assets_ratio: auto;
          --dn-teaser-card--card_image_height: 100%;
          --dn-teaser-card--card_image_picture_height: 100%;
        }

        &:has(.lead) {
          --dn-card_assets_grid-area: 1 / 1 / 6 / 13;
          --dn-card_lead_grid-area: 4 / 1 / span 1 / 13;
          --dn-card_meta_grid-area: 5 / 1 / span 1 / 13;
        }

        picture {
          &[class*='ratio-'] {
            @media only screen and (width < 768px) {
              --dn-image_aspect-ratio: 5 / 7;
            }
          }
        }
      }

      &.breaking,
      &.podcast,
      &.live {
        --dn-teaser-card--badge_spacing-inner: 0 8px 0 3px;
      }

      &.breaking {
        --dn-teaser-card--badge_background: var(--color-teaser-highlight-bg);
        --dn-teaser-card--badge_color: var(--color-teaser-highlight-fg);
      }

      &.exclusive {
        --dn-teaser-card--badge_background: var(--color-teaser-brand-bg);
        --dn-teaser-card--badge_color: var(--color-teaser-brand-fg);
      }

      &.updated,
      &.podcast,
      &.interview,
      &.analysis,
      &.pictures,
      &.video {
        --dn-teaser-card--badge_background: var(--dn-teaser-card--card_background, var(--color-teaser-standard-bg));
        --dn-teaser-card--badge_color: var(--dn-teaser-card--link-color, var(--color-teaser-standard-fg));
      }

      &.live {
        --dn-teaser-card--badge_background: var(--color-badge-live-bg);
        --dn-teaser-card--badge_color: var(--color-badge-live-fg);
      }

      &.dn-image-format-fill {
        --dn-teaser-card--card_image_height: 100%;
        --dn-teaser-card--card_image_picture_height: 100%;

        @media only screen and (width >= 980px) {
          --dn-card_assets_ratio: auto;

          &:not(.dn-image-hide) {
            --dn-teaser-card--card_grid-template: 1fr / repeat(12, 1fr);
          }
        }
      }
    }

    &.layout-a\+ > .dn-teaser-card,
    &.layout-ab > .dn-teaser-card,
    &.layout-ba > .dn-teaser-card,
    &.layout-bb > .dn-teaser-card,
    &.layout-aa > .dn-teaser-card,
    &.layout-abb > .dn-teaser-card,
    &.layout-bba > .dn-teaser-card,
    &.layout-bbabb > .dn-teaser-card {
      &.brand,
      &.highlight,
      &.inverted,
      &.opinion,
      &.brand-secondary,
      &.feature {
        --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
        --dn-teaser-card--title_spacing: 0 var(--teaser-spacing);
        --dn-teaser-card--lead_spacing: 0 var(--teaser-spacing);
        --dn-teaser-card--meta_spacing: var(--row-gap-section) var(--teaser-spacing) 0;
        --dn-teaser-card--strapline_spacing: 0 var(--teaser-spacing);

        @media only screen and (width >= 980px) {
          --dn-teaser-card--meta_spacing: var(--row-gap-section) var(--teaser-spacing) 0;
        }
      }

      .dn-card {
        @container (min-width: 360px) {
          --dn-teaser-card--title_prefered_font-size: 8.5cqi;
        }
      }
    }

    &.layout-a\+ {
      > .dn-teaser-card {
        --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
        --dn-teaser-card--title_font-weight: var(--typography-card-large-font-weight);
        --dn-teaser-card--title_line-height: var(--typography-card-large-line-height);

        .dn-card {
          @container (min-width: 360px) {
            --dn-teaser-card--title_min_font-size: var(--teaser-plus-min-font-size);
            --dn-teaser-card--title_max_font-size: var(--teaser-plus-max-font-size);
          }
        }
      }

      > .brand,
      > .highlight,
      > .inverted,
      > .opinion,
      > .brand-secondary,
      > .feature {
        --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
      }

      > .opinion {
        --dn-teaser-card--title_font-family: var(--typography-card-opinion-font-family);

        .dn-card {
          @container (min-width: 360px) {
            --dn-teaser-card--title_min_font-size: var(--teaser-opinion-plus-min-font-size);
            --dn-teaser-card--title_max_font-size: var(--teaser-opinion-plus-max-font-size);
          }
        }
      }
    }

    &.layout-a,
    &.layout-b {
      --dn-skeleton_gap: var(--row-gap);
      --dn-teaser-card--card_column-gap: var(--row-gap);
      --dn-teaser-card--title_font-weight: var(--typography-card-large-font-weight);

      .dn-teaser-card {
        --dn-teaser-card--card_grid-template: repeat(2, min-content) 1fr / repeat(12, 1fr);
        --dn-teaser-card--card_pseudo_border-bottom-width: 1px;
        --dn-card_badge_grid-area: 1 / 1 / span 4 / 5;
        --dn-card_assets_grid-area: 1 / 1 / 5 / 5;
        --dn-card_title_grid-area: auto / 5 / span 1 / 13;
        --dn-card_lead_grid-area: auto / 5 / span 1 / 13;
        --dn-card_meta_grid-area: -1 / 5 / span 1 / 13;
        --dn-card_kicker_grid-area: 1 / 5 / span 1 / 13;

        &.dn-image-hide {
          --dn-card_title_grid-area: auto / 1 / span 1 / 13;
          --dn-card_lead_grid-area: auto / 1 / span 1 / 13;
          --dn-card_meta_grid-area: auto / 1 / span 1 / 13;
          --dn-card_kicker_grid-area: 1 / 1 / span 1 / 13;
        }

        @media only screen and (width >= 980px) {
          &:not(.opinion) {
            --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
          }

          --dn-teaser-card--card_padding: 0;
          --dn-teaser-card--meta_spacing: var(--row-gap-section) 0 var(--row-gap);
          --dn-card_pseudo_grid-area: -1 / 5 / span 1 / 13;
          --dn-teaser-card--card_pseudo_display: block;
          --dn-teaser-card--card_border-bottom-width: 0;
        }

        &.dn-image-format-auto,
        &.dn-image-format-fill {
          --dn-card_assets_ratio: auto;
          --dn-teaser-card--card_image_height: 100%;
          --dn-teaser-card--card_image_picture_height: 100%;
        }

        &.dn-image-format-fill {
          @media only screen and (width >= 980px) {
            &:not(.dn-image-hide) {
              --dn-teaser-card--card_grid-template: repeat(2, min-content) 1fr / repeat(12, 1fr);
            }
          }
        }

        &.dn-image-right {
          --dn-card_badge_grid-area: 1 / 9 / span 4 / 13;
          --dn-card_assets_grid-area: 1 / 9 / 5 / 13;
          --dn-card_title_grid-area: auto / 1 / span 1 / 9;
          --dn-card_lead_grid-area: auto / 1 / span 1 / 9;
          --dn-card_meta_grid-area: -1 / 1 / span 1 / 9;
          --dn-card_kicker_grid-area: 1 / 1 / span 1 / 9;

          &.dn-image-hide {
            --dn-card_title_grid-area: auto / 1 / span 1 / 13;
            --dn-card_lead_grid-area: auto / 1 / span 1 / 13;
            --dn-card_meta_grid-area: auto / 1 / span 1 / 13;
            --dn-card_kicker_grid-area: 1 / 1 / span 1 / 13;
          }

          &.brand,
          &.highlight,
          &.inverted,
          &.opinion,
          &.brand-secondary,
          &.feature {
            --dn-teaser-card--card_image_border-radius: 0 var(--box-radius) var(--box-radius) 0;
            --dn-teaser-card--assets_spacing: 0;
            --dn-teaser-card--title_spacing: var(--row-gap) 0 0 var(--row-gap);
            --dn-teaser-card--lead_spacing: 0 0 0 var(--row-gap);
            --dn-teaser-card--meta_spacing: var(--row-gap-section) 0 var(--row-gap) var(--row-gap);
            --dn-teaser-card--kicker_spacing: var(--row-gap) 0 0 var(--row-gap);
            --dn-teaser-card--strapline_spacing: 0 0 0 var(--row-gap);

            :has(.kicker) {
              --dn-teaser-card--title_spacing: 0 0 0 var(--row-gap);
            }
          }
        }

        .dn-card {
          @container (min-width: 820px) {
            --dn-teaser-card--title_prefered_font-size: 8.5cqi;
            --dn-teaser-card--title_min_font-size: var(--teaser-b-large-min-font-size);
            --dn-teaser-card--title_max_font-size: var(--teaser-b-large-max-font-size);
          }
        }
      }

      .opinion {
        .dn-card {
          @container (min-width: 920px) {
            --dn-teaser-card--title_prefered_font-size: 8.5cqi;
            --dn-teaser-card--title_min_font-size: var(--teaser-opinion-b-large-min-font-size);
            --dn-teaser-card--title_max_font-size: var(--teaser-opinion-b-large-max-font-size);
          }
        }
      }

      .brand,
      .highlight,
      .inverted,
      .opinion,
      .brand-secondary,
      .feature,
      .special {
        --dn-teaser-card--card_image_border-radius: var(--box-radius) 0 0 var(--box-radius);
        --dn-teaser-card--card_pseudo_border-bottom-width: 0;
        --dn-teaser-card--card_padding: 0;
        --dn-teaser-card--assets_spacing: 0;
        --dn-teaser-card--title_spacing: var(--row-gap) var(--row-gap) 0 0;
        --dn-teaser-card--lead_spacing: 0 var(--row-gap) 0 0;
        --dn-teaser-card--meta_spacing: var(--row-gap-section) var(--row-gap) var(--row-gap) 0;
        --dn-teaser-card--kicker_spacing: var(--row-gap) var(--row-gap) 0 0;
        --dn-teaser-card--strapline_spacing: 0 var(--row-gap) 0 0;

        :has(.kicker) {
          --dn-teaser-card--title_spacing: 0 var(--row-gap) 0 0;
        }

        &.dn-image-hide {
          --dn-teaser-card--card_padding: 0;
          --dn-teaser-card--title_spacing: var(--row-gap) var(--row-gap) 0 var(--row-gap);
          --dn-teaser-card--lead_spacing: 0 var(--row-gap);
          --dn-teaser-card--meta_spacing: 0 var(--row-gap) var(--row-gap) var(--row-gap);
          --dn-teaser-card--kicker_spacing: var(--row-gap) var(--row-gap) 0 var(--row-gap);

          :has(.kicker) {
            --dn-teaser-card--title_spacing: 0 var(--row-gap);
          }
        }
      }

      .special {
        @media only screen and (width >= 980px) {
          --teaser-spacing: 16px;
          --dn-teaser-card--card_assets_overlay: none;
          --dn-teaser-card--card_badge_align-content: flex-end;
          --dn-teaser-card--title_line-height: var(--teaser-line-height);
        }

        .dn-card {
          @container (min-width: 360px) {
            --dn-teaser-card--title_prefered_font-size: 8.5cqi;
            --dn-teaser-card--title_min_font-size: var(--teaserlarge-min-font-size);
            --dn-teaser-card--title_max_font-size: var(--teaserlarge-max-font-size);
          }
        }

        &.dn-teaser-card {
          &.dn-image-mobile-top,
          &:not(.dn-image-mobile-top) {
            @media only screen and (width < 980px) {
              --dn-teaser-card--card_grid-template: 1fr / repeat(12, 1fr);
              --dn-teaser-card--card_image_border-radius: var(--box-radius);
              --dn-teaser-card--card_padding: 0;
              --dn-card_assets_grid-area: 1 / 1 / 5 / 13;
              --dn-card_badge_grid-area: 1 / 1 / span 1 / 13;
              --dn-card_title_grid-area: 3 / 1 / span 1 / 13;
              --dn-card_meta_grid-area: 4 / 1 / span 1 / 13;
              --dn-card_kicker_grid-area: 2 / 1 / span 1 / 13;
              --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
              --dn-teaser-card--title_spacing: 0 var(--teaser-spacing);
              --dn-teaser-card--title_line-height: var(--typography-card-large-line-height);
              --dn-teaser-card--lead_spacing: 0 var(--teaser-spacing);
              --dn-teaser-card--meta_spacing: var(--row-gap-section) var(--teaser-spacing) var(--teaser-spacing);
              --dn-teaser-card--kicker_spacing: 0 var(--teaser-spacing);
              --dn-teaser-card--strapline_spacing: 0 var(--teaser-spacing);

              :has(.kicker) {
                --dn-teaser-card--title_spacing: 0 var(--teaser-spacing);
              }

              picture {
                &[class*='ratio-'] {
                  aspect-ratio: 5 / 7;
                }
              }
            }
          }
        }
      }
    }

    &.layout-a {
      .dn-teaser-card {
        --dn-card_badge_grid-area: 1 / 1 / span 4 / 7;
        --dn-card_assets_grid-area: 1 / 1 / 5 / 7;
        --dn-card_title_grid-area: auto / 7 / span 1 / 13;
        --dn-card_lead_grid-area: auto / 7 / span 1 / 13;
        --dn-card_meta_grid-area: -1 / 7 / span 1 / 13;
        --dn-card_kicker_grid-area: 1 / 7 / span 1 / 13;

        &.dn-image-right {
          --dn-card_badge_grid-area: 1 / 7 / span 4 / 13;
          --dn-card_assets_grid-area: 1 / 7 / 5 / 13;
          --dn-card_title_grid-area: auto / 1 / span 1 / 7;
          --dn-card_lead_grid-area: auto / 1 / span 1 / 7;
          --dn-card_meta_grid-area: -1 / 1 / span 1 / 7;
          --dn-card_kicker_grid-area: 1 / 1 / span 1 / 7;

          @media only screen and (width < 980px) {
            --dn-teaser-card--card_grid-template: repeat(6, min-content) 1fr / repeat(12, 1fr);
            --dn-card_badge_grid-area: 5 / 1 / auto / 13;
            --dn-card_assets_grid-area: 1 / 1 / 6 / 13;
            --dn-card_title_grid-area: auto / 1 / span 1 / 13;
            --dn-card_lead_grid-area: auto / 1 / span 1 / 13;
            --dn-card_meta_grid-area: auto / 1 / span 1 / 13;
            --dn-card_kicker_grid-area: auto / 1 / span 1 / 13;
          }
        }

        @media only screen and (width < 980px) {
          --dn-teaser-card--card_grid-template: repeat(6, min-content) 1fr / repeat(12, 1fr);
          --dn-card_badge_grid-area: 5 / 1 / auto / 13;
          --dn-card_assets_grid-area: 1 / 1 / 6 / 13;
          --dn-card_title_grid-area: auto / 1 / span 1 / 13;
          --dn-card_lead_grid-area: auto / 1 / span 1 / 13;
          --dn-card_meta_grid-area: auto / 1 / span 1 / 13;
          --dn-card_kicker_grid-area: auto / 1 / span 1 / 13;

          &:not(.opinion) {
            --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
          }

          .dn-card {
            @container (min-width: 360px) {
              --dn-teaser-card--title_prefered_font-size: 8.5cqi;
              --dn-teaser-card--title_min_font-size: var(--teaserlarge-min-font-size);
              --dn-teaser-card--title_max_font-size: var(--teaserlarge-max-font-size);
            }
          }

          &.brand,
          &.highlight,
          &.inverted,
          &.opinion,
          &.brand-secondary,
          &.feature {
            --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
            --dn-teaser-card--card_padding: 0 0 var(--teaser-spacing);
            --dn-teaser-card--assets_spacing: 0;
            --dn-teaser-card--title_spacing: 0 var(--row-gap);
            --dn-teaser-card--lead_spacing: 0 var(--row-gap);
            --dn-teaser-card--meta_spacing: 0 var(--row-gap);
            --dn-teaser-card--kicker_spacing: 0 var(--row-gap);

            :has(.kicker) {
              --dn-teaser-card--title_spacing: 0 var(--row-gap);
            }
          }

          &.dn-image-format-auto,
          &.dn-image-format-fill {
            --dn-teaser-card--card_image_height: auto;
            --dn-teaser-card--card_image_picture_height: auto;
          }
        }

        @media only screen and (width >= 980px) {
          --dn-card_pseudo_grid-area: -1 / 7 / span 1 / 13;
        }
      }

      @media only screen and (width >= 980px) {
        --dn-skeleton_grid-template: repeat(2, min-content) 1fr / repeat(12, 1fr);
        --dn-skeleton_media_grid-area: 1 / 1 / 5 / 7;
        --dn-skeleton_lines_grid-area: 1 / 7 / 5 / 13;
      }
    }

    &.layout-aa,
    &.layout-bb,
    &.layout-ab,
    &.layout-ba {
      --dn-teaser-card--card_grid-template: repeat(6, min-content) 1fr / repeat(12, 1fr);
      --dn-teaser-card--title_font-weight: var(--typography-card-large-font-weight);

      @media only screen and (width >= 980px) {
        --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
      }

      > .brand,
      > .highlight,
      > .inverted,
      > .opinion,
      > .brand-secondary,
      > .feature {
        --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
      }
    }

    &.layout-aa {
      &:not(.opinion) {
        --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
      }
    }

    &.layout-bb,
    &.layout-ab,
    &.layout-ba {
      > :not(.dn-image-mobile-top) {
        @media only screen and (width < 980px) {
          --dn-teaser-card--kicker_font-family: var(--typography-utility-s-font-family);
          --dn-teaser-card--kicker_font-size: var(--typography-utility-s-font-size);
          --dn-teaser-card--kicker_font-weight: var(--typography-utility-s-font-weight);
          --dn-teaser-card--kicker_line-height: 100%;
        }
      }
    }

    &.layout-abb,
    &.layout-bba,
    &.layout-bbabb {
      --dn-teaser-card--card_grid-template: repeat(6, min-content) 1fr / repeat(12, 1fr);
    }

    &.layout-bb {
      > .dn-teaser-card {
        .dn-card {
          @container (min-width: 430px) {
            --dn-teaser-card--title_min_font-size: var(--teaser-b-large-min-font-size);
            --dn-teaser-card--title_max_font-size: var(--teaser-b-large-max-font-size);
          }
        }
      }
    }

    &.layout-abb {
      > :nth-child(1) {
        &.brand,
        &.highlight,
        &.inverted,
        &.opinion,
        &.brand-secondary,
        &.feature {
          --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
        }

        &:not(.opinion) {
          --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
        }

        @media only screen and (width >= 980px) {
          --dn-skeleton_aspect-ratio: auto;

          &.dn-image-format-auto {
            --dn-card_assets_ratio: auto;
            --dn-teaser-card--card_image_height: 100%;
            --dn-teaser-card--card_image_picture_height: 100%;

            &:not(.dn-image-hide) {
              --dn-teaser-card--card_grid-template: 1fr / repeat(12, 1fr);
            }
          }
        }
      }

      > :nth-child(n + 2) {
        @media only screen and (width < 980px) {
          &:not(.dn-image-mobile-top) {
            --dn-teaser-card--kicker_font-family: var(--typography-utility-s-font-family);
            --dn-teaser-card--kicker_font-size: var(--typography-utility-s-font-size);
            --dn-teaser-card--kicker_font-weight: var(--typography-utility-s-font-weight);
            --dn-teaser-card--kicker_line-height: 100%;
          }
        }
      }
    }

    &.layout-bba {
      > :nth-child(3) {
        &.brand,
        &.highlight,
        &.inverted,
        &.opinion,
        &.brand-secondary,
        &.feature {
          --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
        }

        &:not(.opinion) {
          --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
        }

        @media only screen and (width >= 980px) {
          --dn-skeleton_aspect-ratio: auto;

          &.dn-image-format-auto {
            --dn-card_assets_ratio: auto;
            --dn-teaser-card--card_image_height: 100%;
            --dn-teaser-card--card_image_picture_height: 100%;

            &:not(.dn-image-hide) {
              --dn-teaser-card--card_grid-template: 1fr / repeat(12, 1fr);
            }
          }
        }
      }

      > :not(:nth-child(3)) {
        @media only screen and (width < 980px) {
          &:not(.dn-image-mobile-top) {
            --dn-teaser-card--kicker_font-family: var(--typography-utility-s-font-family);
            --dn-teaser-card--kicker_font-size: var(--typography-utility-s-font-size);
            --dn-teaser-card--kicker_font-weight: var(--typography-utility-s-font-weight);
            --dn-teaser-card--kicker_line-height: 100%;
          }
        }
      }
    }

    &.layout-abb,
    &.layout-bba,
    &.layout-bb {
      .dn-teaser-card-b {
        &.dn-image-mobile {
          &-top {
            @media only screen and (width < 980px) {
              --dn-grid-item_grid-column: 1 / 3;

              &:not(.opinion) {
                --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
              }

              .dn-card {
                @container (min-width: 360px) {
                  --dn-teaser-card--title_prefered_font-size: 8.5cqi;
                  --dn-teaser-card--title_min_font-size: var(--teaserlarge-min-font-size);
                  --dn-teaser-card--title_max_font-size: var(--teaserlarge-max-font-size);
                }
              }
            }
          }

          &-top + .dn-teaser-card-b:not(.dn-image-mobile-top),
          &-auto:has(+ .dn-teaser-card-b.dn-image-mobile-top) {
            @media only screen and (width < 980px) {
              --dn-grid-item_grid-column: 1 / 3;
              --dn-teaser-card--card_grid-template: repeat(2, min-content) 1fr / repeat(12, 1fr);
              --dn-teaser-card--card_image_height: 100%;
              --dn-teaser-card--card_image_picture_height: 100%;
              --dn-teaser-card--kicker_font-size: var(--typography-utility-m-font-size);
              --dn-card_badge_grid-area: 1 / 1 / span 3 / 5;
              --dn-card_assets_grid-area: 1 / 1 / 4 / 5;
              --dn-card_title_grid-area: auto / 5 / span 1 / 13;
              --dn-card_lead_grid-area: auto / 5 / span 1 / 13;
              --dn-card_meta_grid-area: -2 / 5 / span 1 / 13;
              --dn-card_kicker_grid-area: 1 / 5 / span 1 / 13;

              &.dn-image-hide {
                --dn-card_title_grid-area: auto / 1 / span 1 / 13;
                --dn-card_lead_grid-area: auto / 1 / span 1 / 13;
                --dn-card_meta_grid-area: -2 / 1 / span 1 / 13;
                --dn-card_kicker_grid-area: 1 / 1 / span 1 / 13;
              }

              picture {
                &[class*='ratio-'] {
                  --dn-image_aspect-ratio: 1 / 1;
                }
              }
            }

            &.brand,
            &.highlight,
            &.inverted,
            &.opinion,
            &.brand-secondary,
            &.feature {
              @media only screen and (width < 980px) {
                --dn-teaser-card--card_image_border-radius: var(--box-radius) 0 0 var(--box-radius);
                --dn-teaser-card--card_padding: 0;
                --dn-teaser-card--assets_spacing: 0;
                --dn-teaser-card--title_spacing: var(--row-gap) var(--row-gap) 0 0;
                --dn-teaser-card--lead_spacing: 0 var(--row-gap) 0 0;
                --dn-teaser-card--meta_spacing: 0 var(--row-gap) var(--row-gap) 0;
                --dn-teaser-card--kicker_spacing: var(--row-gap) var(--row-gap) 0 0;
                --dn-teaser-card--strapline_spacing: 0 var(--row-gap) 0 0;

                :has(.kicker) {
                  --dn-teaser-card--title_spacing: 0 var(--row-gap) 0 0;
                }
              }
            }
          }
        }
      }

      &:has(.dn-teaser-card-b\+):not(:has(.dn-teaser-card-b\+ ~ .dn-teaser-card-b\+, .dn-image-mobile-top)) {
        @media only screen and (width < 980px) {
          --dn-grid_grid-template: 1fr / repeat(5, 1fr);
          --dn-teaser-card--card_image_height: 100%;
          --dn-teaser-card--card_image_picture_height: 100%;

          .dn-teaser-card {
            --dn-teaser-card--card_grid-template: 1fr / repeat(12, 1fr);

            &-a {
              --dn-grid-item_grid-column: 1 / 6;
              --dn-grid-item_grid-row: 1 / auto;
            }

            &-b {
              --dn-grid-item_grid-column: 1 / 3;
              --dn-grid-item_grid-row: auto;

              &\+ {
                --dn-grid-item_grid-column: 1 / 4;

                + .dn-teaser-card-b {
                  --dn-grid-item_grid-column: 4 / 6;
                }
              }

              + .dn-teaser-card-b\+ {
                --dn-grid-item_grid-column: 3 / 6;
              }
            }
          }
        }
      }
    }

    &.layout-bbabb,
    &.layout-bbbabbb {
      > :nth-child(1) {
        --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
        --dn-teaser-card--title_font-weight: var(--typography-card-large-font-weight);

        @media only screen and (width >= 980px) {
          --dn-skeleton_aspect-ratio: auto;

          &.dn-image-format-auto {
            --dn-card_assets_ratio: auto;
            --dn-teaser-card--card_image_height: 100%;
            --dn-teaser-card--card_image_picture_height: 100%;

            &:not(.dn-image-hide) {
              --dn-teaser-card--card_grid-template: 1fr / repeat(12, 1fr);
            }
          }
        }

        .dn-card {
          @container (min-width: 360px) {
            --dn-teaser-card--title_prefered_font-size: 7cqi;
          }
        }
      }

      > :nth-child(n + 2) {
        @media only screen and (width < 980px) {
          &:not(.dn-image-mobile-top) {
            --dn-teaser-card--kicker_font-family: var(--typography-utility-s-font-family);
            --dn-teaser-card--kicker_font-size: var(--typography-utility-s-font-size);
            --dn-teaser-card--kicker_font-weight: var(--typography-utility-s-font-weight);
            --dn-teaser-card--kicker_line-height: 100%;
          }
        }
      }
    }

    &.layout-bbb,
    &.layout-bab,
    &.layout-bbbb {
      @media only screen and (width < 980px) {
        .dn-teaser-card {
          &.brand,
          &.highlight,
          &.inverted,
          &.opinion,
          &.brand-secondary,
          &.feature {
            --dn-teaser-card--card_padding: 0;
            --dn-teaser-card--title_spacing: var(--row-gap-section) var(--row-gap-section) 0 0;
            --dn-teaser-card--kicker_spacing: var(--row-gap-section) var(--row-gap-section) 0 0;

            :has(.kicker) {
              --dn-teaser-card--title_spacing: 0 var(--row-gap-section) 0 0;
            }

            &.dn-image-hide {
              --dn-teaser-card--title_spacing: var(--row-gap-section) var(--row-gap-section) 0 var(--row-gap-section);
              --dn-teaser-card--lead_spacing: var(--row-gap-section) var(--row-gap-section) 0 var(--row-gap-section);
              --dn-teaser-card--kicker_spacing: var(--row-gap-section) var(--row-gap-section) 0 var(--row-gap-section);
              --dn-teaser-card--meta_spacing: 0 var(--row-gap-section) var(--row-gap-section) var(--row-gap-section);

              :has(.kicker) {
                --dn-teaser-card--title_spacing: 0 var(--row-gap-section);
              }
            }
          }

          --dn-teaser-card--card_grid-template: repeat(2, min-content) 1fr / repeat(12, 1fr);
          --dn-teaser-card--card_column-gap: var(--row-gap);
          --dn-card_badge_grid-area: 1 / 1 / span 3 / 5;
          --dn-card_assets_grid-area: 1 / 1 / 4 / 5;
          --dn-card_title_grid-area: auto / 5 / auto / 13;
          --dn-card_lead_grid-area: auto / 5 / auto / 13;
          --dn-card_meta_grid-area: -2 / 5 / span 1 / 13;
          --dn-card_kicker_grid-area: 1 / 5 / span 1 / 13;

          &.dn-image-hide {
            --dn-card_title_grid-area: auto / 1 / auto / 13;
            --dn-card_lead_grid-area: auto / 1 / auto / 13;
            --dn-card_meta_grid-area: -2 / 1 / span 1 / 13;
            --dn-card_kicker_grid-area: 1 / 1 / span 1 / 13;
          }

          &.dn-image-format-auto,
          &.dn-image-format-fill {
            --dn-card_assets_ratio: auto;
            --dn-teaser-card--card_image_height: 100%;
            --dn-teaser-card--card_image_picture_height: 100%;
          }
        }
      }
    }

    &.layout-aa > .dn-teaser-card,
    &.layout-abb > :nth-child(1),
    &.layout-bba > :nth-child(3),
    &.layout-ab > :nth-child(1),
    &.layout-ba > :nth-child(2),
    &.layout-bbabb > :nth-child(1),
    &.layout-bbbabbb > :nth-child(1) {
      .dn-card {
        @container (min-width: 360px) {
          --dn-teaser-card--title_min_font-size: var(--teaser-a-large-min-font-size);
          --dn-teaser-card--title_max_font-size: var(--teaser-a-large-max-font-size);
        }
      }

      &.opinion {
        .dn-card {
          @container (min-width: 360px) {
            --dn-teaser-card--title_min_font-size: var(--teaser-opinion-a-large-min-font-size);
            --dn-teaser-card--title_max_font-size: var(--teaser-opinion-a-large-max-font-size);
          }
        }
      }
    }

    &.layout-ab > :nth-child(2),
    &.layout-ba > :nth-child(1) {
      @media only screen and (width >= 980px) {
        --dn-skeleton_aspect-ratio: auto;
      }
    }

    &.layout-b,
    &.layout-bbb,
    &.layout-bab,
    &.layout-bbbb {
      .dn-teaser-card {
        &:not(.dn-image-mobile-top, .dn-job-item) {
          @media only screen and (width < 980px) {
            --dn-teaser-card--kicker_font-family: var(--typography-utility-s-font-family);
            --dn-teaser-card--kicker_font-weight: var(--typography-utility-s-font-weight);
            --dn-teaser-card--kicker_line-height: 100%;
          }

          picture {
            &[class*='ratio-'] {
              @media only screen and (width < 768px) {
                --dn-image_aspect-ratio: 1 / 1;
              }
            }
          }
        }

        &.dn-image-mobile-top {
          @media only screen and (width < 980px) {
            --dn-teaser-card--card_grid-template: repeat(6, min-content) 1fr / repeat(12, 1fr);
            --dn-teaser-card--card_padding: 0 0 var(--teaser-spacing);
            --dn-card_badge_grid-area: 5 / 1 / auto / 13;
            --dn-card_assets_grid-area: 1 / 1 / 6 / 13;
            --dn-card_kicker_grid-area: auto / 1 / span 1 / 13;
            --dn-card_title_grid-area: auto / 1 / span 1 / 13;
            --dn-card_lead_grid-area: auto / 5 / span 1 / 13;
            --dn-card_meta_grid-area: auto / 1 / span 1 / 13;

            &.brand,
            &.highlight,
            &.inverted,
            &.opinion,
            &.brand-secondary,
            &.feature {
              --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
              --dn-teaser-card--kicker_spacing: 0 var(--teaser-spacing);
              --dn-teaser-card--title_spacing: 0 var(--teaser-spacing);
              --dn-teaser-card--lead_spacing: 0 var(--teaser-spacing);
              --dn-teaser-card--meta_spacing: var(--row-gap-section) var(--teaser-spacing) 0;
              --dn-teaser-card--strapline_spacing: 0 var(--teaser-spacing);

              :has(.kicker) {
                --dn-teaser-card--title_spacing: 0 var(--teaser-spacing);
              }
            }

            &:not(.opinion) {
              --dn-teaser-card--title_font-family: var(--typography-card-large-font-family);
            }

            .dn-card {
              @container (min-width: 360px) {
                --dn-teaser-card--title_prefered_font-size: 8.5cqi;
                --dn-teaser-card--title_min_font-size: var(--teaserlarge-min-font-size);
                --dn-teaser-card--title_max_font-size: var(--teaserlarge-max-font-size);
              }
            }
          }
        }
      }
    }

    &.layout-b {
      &:has(.dn-skeleton-media) {
        --dn-skeleton_grid-template: repeat(2, min-content) 1fr / repeat(12, 1fr);
        --dn-skeleton_media_grid-area: 1 / 1 / 5 / 5;
        --dn-skeleton_lines_grid-area: 1 / 5 / 5 / 13;

        @media only screen and (width < 980px) {
          --dn-skeleton_aspect-ratio: 1 / 1;
        }
      }
    }

    &.dn-inline-ad {
      &.layout-bb {
        --dn-grid_grid-template: auto / repeat(1, 1fr);

        @media only screen and (width >= 980px) {
          --dn-grid_grid-template: auto / repeat(2, 1fr);
        }

        &:has(.hide-ads),
        &:has(.ad-group-child:empty) {
          --dn-grid_grid-template: auto / repeat(1, 1fr);
        }
      }
    }
  }

  .dn-collection {
    &.brand {
      --dn-collection_fill: var(--color-teaser-brand-bg);
      --dn-headline_color: var(--color-teaser-brand-fg);
      --dn-placeholder--logo_color: var(--color-teaser-brand-fg);
      --dn-placeholder--readmore_color: var(--color-teaser-brand-fg);
      --dn-placeholder--readmore_color__hover: var(--color-teaser-brand-fg);
      --dn-placeholder--readmore_color__active: var(--color-teaser-brand-fg);
      --dn-placeholder--readmore-icon_color: var(--color-teaser-brand-fg);
      --dn-skeleton_lines_border-bottom-color: var(--color-teaser-brand-divider);

      .dn-grid {
        .dn-teaser-card {
          --dn-teaser-card--card_border-bottom-color: var(--color-teaser-brand-divider);
          --dn-teaser-card--link-color: var(--color-teaser-brand-fg);
          --dn-teaser-card--link-color__hover: var(--color-teaser-brand-fg);
          --dn-teaser-card--link-color__active: var(--color-teaser-brand-fg);
          --dn-teaser-card--meta_color: var(--color-teaser-brand-subtle-fg);
          --dn-teaser-card--kicker_color: var(--color-teaser-brand-fg);
        }
      }
    }

    &.brand-secondary {
      --dn-collection_fill: var(--color-teaser-brand-secondary-bg);
      --dn-headline_color: var(--color-teaser-brand-secondary-fg);
      --dn-placeholder--logo_color: var(--color-teaser-brand-secondary-fg);
      --dn-placeholder--readmore_color: var(--color-teaser-brand-secondary-fg);
      --dn-placeholder--readmore_color__hover: var(--color-teaser-brand-secondary-fg);
      --dn-placeholder--readmore_color__active: var(--color-teaser-brand-secondary-fg);
      --dn-placeholder--readmore-icon_color: var(--color-teaser-brand-secondary-fg);
      --dn-skeleton_lines_border-bottom-color: var(--color-teaser-brand-secondary-divider);

      .dn-grid {
        .dn-teaser-card {
          --dn-teaser-card--card_border-bottom-color: var(--color-teaser-brand-secondary-divider);
          --dn-teaser-card--link-color: var(--color-teaser-brand-secondary-fg);
          --dn-teaser-card--link-color__hover: var(--color-teaser-brand-secondary-fg);
          --dn-teaser-card--link-color__active: var(--color-teaser-brand-secondary-fg);
          --dn-teaser-card--meta_color: var(--color-teaser-brand-secondary-subtle-fg);
          --dn-teaser-card--kicker_color: var(--color-teaser-brand-secondary-fg);
        }
      }
    }

    &.feature {
      --dn-collection_fill: var(--color-teaser-feature-bg);
      --dn-headline_color: var(--color-teaser-feature-fg);
      --dn-placeholder--logo_color: var(--color-teaser-feature-fg);
      --dn-placeholder--readmore_color: var(--color-teaser-feature-fg);
      --dn-placeholder--readmore_color__hover: var(--color-teaser-feature-fg);
      --dn-placeholder--readmore_color__active: var(--color-teaser-feature-fg);
      --dn-placeholder--readmore-icon_color: var(--color-teaser-feature-fg);
      --dn-skeleton_lines_border-bottom-color: var(--color-teaser-feature-divider);

      .dn-grid {
        .dn-teaser-card {
          --dn-teaser-card--card_border-bottom-color: var(--color-teaser-feature-divider);
          --dn-teaser-card--link-color: var(--color-teaser-feature-fg);
          --dn-teaser-card--link-color__hover: var(--color-teaser-feature-fg);
          --dn-teaser-card--link-color__active: var(--color-teaser-feature-fg);
          --dn-teaser-card--meta_color: var(--color-teaser-brand-feature-fg);
          --dn-teaser-card--kicker_color: var(--color-teaser-brand-feature-fg);
        }
      }
    }

    &.highlight {
      --dn-collection_fill: var(--color-teaser-highlight-bg);
      --dn-headline_color: var(--color-teaser-highlight-fg);
      --dn-placeholder--logo_color: var(--color-teaser-highlight-fg);
      --dn-placeholder--readmore_color: var(--color-teaser-highlight-fg);
      --dn-placeholder--readmore_color__hover: var(--color-teaser-highlight-fg);
      --dn-placeholder--readmore_color__active: var(--color-teaser-highlight-fg);
      --dn-placeholder--readmore-icon_color: var(--color-teaser-highlight-fg);
      --dn-skeleton_lines_border-bottom-color: var(--color-teaser-highlight-divider);

      .dn-grid {
        .dn-teaser-card {
          --dn-teaser-card--card_border-bottom-color: var(--color-teaser-highlight-divider);
          --dn-teaser-card--link-color: var(--color-teaser-highlight-fg);
          --dn-teaser-card--link-color__hover: var(--color-teaser-highlight-fg);
          --dn-teaser-card--link-color__active: var(--color-teaser-highlight-fg);
          --dn-teaser-card--meta_color: var(--color-teaser-highlight-subtle-fg);
          --dn-teaser-card--kicker_color: var(--color-teaser-highlight-fg);
        }
      }
    }

    &.inverted {
      --dn-collection_fill: var(--color-teaser-inverted-bg);
      --dn-headline_color: var(--color-teaser-inverted-fg);
      --dn-placeholder--logo_color: var(--color-teaser-inverted-fg);
      --dn-placeholder--readmore_color: var(--color-teaser-inverted-fg);
      --dn-placeholder--readmore_color__hover: var(--color-teaser-inverted-fg);
      --dn-placeholder--readmore_color__active: var(--color-teaser-inverted-fg);
      --dn-placeholder--readmore-icon_color: var(--color-teaser-inverted-fg);
      --dn-skeleton_lines_border-bottom-color: var(--color-teaser-inverted-divider);

      .dn-grid {
        .dn-teaser-card {
          --dn-teaser-card--card_border-bottom-color: var(--color-teaser-inverted-divider);
          --dn-teaser-card--link-color: var(--color-teaser-inverted-fg);
          --dn-teaser-card--link-color__hover: var(--color-teaser-inverted-fg);
          --dn-teaser-card--link-color__active: var(--color-teaser-inverted-fg);
          --dn-teaser-card--meta_color: var(--color-teaser-inverted-subtle-fg);
          --dn-teaser-card--kicker_color: var(--color-teaser-inverted-fg);
        }
      }
    }

    &.opinion {
      --dn-collection_fill: var(--color-teaser-opinion-bg);
      --dn-headline_color: var(--color-teaser-opinion-fg);
      --dn-placeholder--logo_color: var(--color-teaser-opinion-fg);
      --dn-placeholder--readmore_color: var(--color-teaser-opinion-fg);
      --dn-placeholder--readmore_color__hover: var(--color-teaser-opinion-fg);
      --dn-placeholder--readmore_color__active: var(--color-teaser-opinion-fg);
      --dn-placeholder--readmore-icon_color: var(--color-teaser-opinion-fg);
      --dn-skeleton_lines_border-bottom-color: var(--color-teaser-opinion-divider);

      .dn-grid {
        .dn-teaser-card {
          --dn-teaser-card--card_border-bottom-color: var(--color-teaser-opinion-divider);
          --dn-teaser-card--link-color: var(--color-teaser-opinion-fg);
          --dn-teaser-card--link-color__hover: var(--color-teaser-opinion-fg);
          --dn-teaser-card--link-color__active: var(--color-teaser-opinion-fg);
          --dn-teaser-card--meta_color: var(--color-teaser-opinion-subtle-fg);
          --dn-teaser-card--kicker_color: var(--color-teaser-opinion-fg);
          --dn-teaser-card--title_font-family: var(--typography-card-opinion-font-family);
          --dn-teaser-card--title_min_font-size: var(--teaser-opinion-medium-min-font-size);
          --dn-teaser-card--title_max_font-size: var(--teaser-opinion-medium-max-font-size);
          --dn-teaser-card--title_font-weight: var(--typography-card-opinion-font-weight);
        }

        &.layout-bbabb {
          > :nth-child(3) {
            .dn-card {
              @container (min-width: 360px) {
                --dn-teaser-card--title_min_font-size: var(--teaser-opinion-a-large-min-font-size);
                --dn-teaser-card--title_max_font-size: var(--teaser-opinion-a-large-max-font-size);
              }
            }
          }
        }
      }
    }

    &.brand,
    &.highlight,
    &.inverted,
    &.opinion,
    &.brand-secondary,
    &.feature {
      --dn-job-carousel-desktop_padding-multiply: 4;
      --dn-job-carousel-mobile_padding-multiply: 6;

      .layout-ab,
      .layout-ba,
      .layout-bb,
      .layout-aa {
        > .dn-teaser-card {
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
        }

        --dn-skeleton_lines_border-bottom-width: 0;
        --dn-skeleton_lines_padding: 0;
      }

      .layout-a\+ {
        > .dn-teaser-card {
          --dn-teaser-card--title_spacing: 0 var(--row-gap);
          --dn-teaser-card--lead_spacing: 0 var(--row-gap);
          --dn-teaser-card--meta_spacing: 0 var(--row-gap);
          --dn-teaser-card--strapline_spacing: 0 var(--row-gap);
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0 0 var(--row-gap);
        }
      }

      .layout-abb,
      .layout-bba,
      .layout-bbb,
      .layout-bab,
      .layout-bbbb,
      .layout-bbabb {
        > .dn-teaser-card {
          --dn-teaser-card--card_image_border-radius: var(--box-radius);
        }
      }

      .layout-abb {
        > :nth-child(1) {
          @media only screen and (width >= 980px) {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
            --dn-skeleton_lines_border-bottom-width: 0;
            --dn-skeleton_lines_padding: 0;
          }
        }

        > :nth-child(3) {
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: 0;
        }
      }

      .layout-bba {
        > :nth-child(2) {
          @media only screen and (width >= 980px) {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
            --dn-skeleton_lines_border-bottom-width: 0;
            --dn-skeleton_lines_padding: 0;
          }
        }

        > :nth-child(3) {
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: 0;
        }
      }

      .layout-bbb,
      .layout-bab,
      .layout-bbbb {
        > .dn-teaser-card {
          @media only screen and (width >= 980px) {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
          }

          @media only screen and (width < 980px) {
            &:nth-last-child(1) {
              --dn-teaser-card--card_border-bottom-width: 0;
              --dn-teaser-card--card_padding: 0;
            }
          }
        }

        @media only screen and (width >= 980px) {
          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: 0;
        }

        > :nth-last-child(1) {
          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: 0;
        }
      }

      .layout-bbabb {
        > :nth-child(1) {
          @media only screen and (width >= 980px) {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
            --dn-skeleton_lines_border-bottom-width: 0;
            --dn-skeleton_lines_padding: 0;
          }
        }

        > :nth-child(4),
        > :nth-child(5) {
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: 0;
        }
      }

      .layout-bbbabbb {
        > :nth-child(1) {
          @media only screen and (width >= 980px) {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
            --dn-skeleton_lines_border-bottom-width: 0;
            --dn-skeleton_lines_padding: 0;
          }
        }

        > :nth-child(6),
        > :nth-child(7) {
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: 0;
        }
      }

      .layout-a,
      .layout-b {
        > .dn-teaser-card {
          --dn-teaser-card--card_image_border-radius: var(--box-radius) 0 0 var(--box-radius);
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
          --dn-teaser-card--kicker_spacing: var(--row-gap) var(--row-gap) 0 0;
          --dn-teaser-card--title_spacing: var(--row-gap) var(--row-gap) 0 0;
          --dn-teaser-card--lead_spacing: 0 var(--row-gap) 0 0;
          --dn-teaser-card--meta_spacing: 0 var(--row-gap) var(--row-gap) 0;
          --dn-teaser-card--strapline_spacing: 0 var(--row-gap) 0 0;

          &:has(.kicker) {
            --dn-teaser-card--title_spacing: 0 var(--row-gap) 0 0;

            @media only screen and (width < 980px) {
              --dn-teaser-card--title_spacing: 0 var(--row-gap);
            }
          }

          &.dn-image-right {
            --dn-teaser-card--card_image_border-radius: 0 var(--box-radius) var(--box-radius) 0;
            --dn-teaser-card--assets_spacing: 0;
            --dn-teaser-card--kicker_spacing: var(--row-gap) 0 0 var(--row-gap);
            --dn-teaser-card--title_spacing: var(--row-gap) 0 0 var(--row-gap);
            --dn-teaser-card--lead_spacing: 0 0 0 var(--row-gap);
            --dn-teaser-card--meta_spacing: 0 0 var(--row-gap) var(--row-gap);
            --dn-teaser-card--strapline_spacing: 0 0 0 var(--row-gap);

            &:has(.kicker) {
              --dn-teaser-card--title_spacing: 0 0 0 var(--row-gap);
            }
          }
        }

        --dn-skeleton_lines_border-bottom-width: 0;
        --dn-skeleton_lines_padding: 0;
      }

      .layout-a {
        @media only screen and (width < 980px) {
          > .dn-teaser-card {
            --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
            --dn-teaser-card--card_padding: 0 0 var(--teaser-spacing);
            --dn-teaser-card--kicker_spacing: 0 var(--row-gap);
            --dn-teaser-card--title_spacing: 0 var(--row-gap);
            --dn-teaser-card--lead_spacing: 0 var(--row-gap);
            --dn-teaser-card--meta_spacing: 0 var(--row-gap);

            &.dn-image-right {
              --dn-teaser-card--card_padding: 0 0 var(--teaser-spacing);
              --dn-teaser-card--kicker_spacing: 0 var(--row-gap);
              --dn-teaser-card--title_spacing: 0 var(--row-gap);
              --dn-teaser-card--lead_spacing: 0 var(--row-gap);
              --dn-teaser-card--meta_spacing: 0 var(--row-gap);

              &:has(.kicker) {
                --dn-teaser-card--title_spacing: 0 var(--row-gap);
              }
            }
          }
        }
      }
    }
  }
}
</style>
